import { useEffect, useRef, useState } from "react";
import { ScanLine, Loader2, PackageOpen } from "lucide-react";
import { api } from "@/lib/api";
import { imgUrl, fmtGram } from "@/lib/format";
import { TrayInput } from "@/components/TrayInput";
import { toast } from "sonner";

export default function TrayScan() {
  const [tray, setTray] = useState("");
  const [saved, setSaved] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    api.get("/admin/trays").then((r) => setSaved(r.data)).catch(() => {});
  }, []);

  const lookup = async (name) => {
    const t = (name || "").trim().toUpperCase();
    if (!t) { setProducts([]); setSearched(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tray-products", { params: { tray: t } });
      setProducts(data.products || []);
      setSearched(true);
    } catch (err) {
      toast.error("Ürünler getirilemedi");
    } finally {
      setLoading(false);
    }
  };

  const debounceRef = useRef(null);
  const onChange = (v) => {
    setTray(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (v && v.trim().length >= 2) debounceRef.current = setTimeout(() => lookup(v), 300);
    else { setProducts([]); setSearched(false); }
  };

  return (
    <div data-testid="tray-scan-page">
      <div className="flex items-center gap-2 mb-1">
        <ScanLine className="text-gold" size={22} />
        <h1 className="font-display text-3xl text-foreground">Tabla Tara</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Tabla barkodunu okutun veya adını yazın; o tablaya atanmış ürünler listelenir.
      </p>

      <div className="max-w-md mb-4">
        <TrayInput value={tray} onChange={onChange} testid="tray-scan-input" className="bg-elevated border-border" />
      </div>

      {saved.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="tray-quick-list">
          {saved.map((t) => (
            <button
              key={t.id}
              data-testid={`tray-quick-${t.name}`}
              onClick={() => { setTray(t.name); lookup(t.name); }}
              className={`px-3 h-8 rounded-full border text-sm transition-colors ${tray.trim().toUpperCase() === t.name ? "border-gold text-gold bg-gold/10" : "border-border text-foreground hover:border-gold"}`}
            >
              {t.name}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex py-12 justify-center"><Loader2 className="animate-spin text-gold" size={26} /></div>
      ) : searched && (
        <div data-testid="tray-scan-results">
          <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
            <PackageOpen size={16} className="text-gold" />
            <span data-testid="tray-scan-count"><b className="text-foreground">{tray.trim().toUpperCase()}</b> tablasında {products.length} ürün</span>
          </div>
          {products.length === 0 ? (
            <p className="text-muted-foreground text-sm py-12 text-center border border-dashed border-border rounded-lg">
              Bu tablaya atanmış ürün bulunamadı.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="tray-scan-grid">
              {products.map((p) => (
                <div key={p.id} data-testid={`tray-product-${p.code}`} className="bg-surface border border-border rounded-lg overflow-hidden">
                  <div className="aspect-square bg-obsidian flex items-center justify-center">
                    {p.image_url ? <img src={imgUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover" /> : <PackageOpen size={28} className="text-muted-foreground/40" />}
                  </div>
                  <div className="p-3">
                    <p className="text-xs font-mono text-gold">{p.code}</p>
                    <p className="text-sm text-foreground truncate">{p.name || "-"}</p>
                    <p className="text-xs text-muted-foreground mt-1">{p.karat ? `${p.karat} Ayar` : ""} {p.gram ? `· ${fmtGram(p.gram)} gr` : ""}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
